import type {
  AdminOverview,
  ChannelInfo,
  InvitePreview,
  Lap,
  LeaderboardEntry,
  Readiness,
  SameTrackSession,
  Team,
  TeamDetail,
  TeamInvite,
  TelemetrySession,
  TraceResponse,
  User,
} from "./types";

/** Identifies one lap of one session, which is what a cross-driver overlay needs. */
export interface LapRef {
  sessionId: string;
  lap: number;
}

export function formatLapRef({ sessionId, lap }: LapRef): string {
  return `${sessionId}:${lap}`;
}

export function parseLapRefs(raw: string | null): LapRef[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => part.split(":"))
    .filter((parts) => parts.length === 2 && parts[0] && Number.isFinite(Number(parts[1])))
    .map(([sessionId, lap]) => ({ sessionId, lap: Number(lap) }));
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    // Session lives in an HttpOnly cookie.
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let code = "unknown";
    let message = response.statusText;
    try {
      const body = await response.json();
      code = body.error ?? code;
      message = body.message ?? message;
    } catch {
      // Non-JSON error body, keep the status text.
    }
    throw new ApiError(response.status, code, message);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const api = {
  readiness: () => request<Readiness>("/api/health/ready"),

  me: () => request<User>("/api/auth/me"),

  logout: () => request<{ status: string }>("/api/auth/logout", { method: "POST" }),

  steamLoginUrl: (redirectTo = "/") =>
    `/api/auth/steam/login?redirect_to=${encodeURIComponent(redirectTo)}`,

  channels: () => request<ChannelInfo[]>("/api/telemetry/channels"),

  listSessions: () => request<TelemetrySession[]>("/api/telemetry/sessions"),

  getSession: (id: string) => request<TelemetrySession>(`/api/telemetry/sessions/${id}`),

  deleteSession: (id: string) =>
    request<{ status: string }>(`/api/telemetry/sessions/${id}`, { method: "DELETE" }),

  setSessionPrivate: (id: string, isPrivate: boolean) =>
    request<TelemetrySession>(`/api/telemetry/sessions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ is_private: isPrivate }),
    }),

  listLaps: (id: string) => request<Lap[]>(`/api/telemetry/sessions/${id}/laps`),

  /** Your own sessions on the track `id` was driven on, newest first. */
  sameTrackSessions: (id: string) =>
    request<SameTrackSession[]>(`/api/telemetry/sessions/${id}/same-track`),

  getTraces: (id: string, laps: number[], channels: string[]) =>
    request<TraceResponse>(
      `/api/telemetry/sessions/${id}/traces?laps=${laps.join(",")}` +
        `&channels=${channels.join(",")}`,
    ),

  compareTraces: (laps: LapRef[], channels: string[]) =>
    request<TraceResponse>(
      `/api/telemetry/compare?laps=${laps.map(formatLapRef).join(",")}` +
        `&channels=${channels.join(",")}`,
    ),

  listTeams: () => request<Team[]>("/api/teams"),

  getTeam: (id: string) => request<TeamDetail>(`/api/teams/${id}`),

  createTeam: (name: string) =>
    request<Team>("/api/teams", { method: "POST", body: JSON.stringify({ name }) }),

  updateTeam: (id: string, changes: { name?: string; leaderboard_public?: boolean }) =>
    request<Team>(`/api/teams/${id}`, {
      method: "PATCH",
      body: JSON.stringify(changes),
    }),

  deleteTeam: (id: string) =>
    request<{ status: string }>(`/api/teams/${id}`, { method: "DELETE" }),

  removeTeamMember: (teamId: string, userId: string) =>
    request<{ status: string }>(`/api/teams/${teamId}/members/${userId}`, {
      method: "DELETE",
    }),

  createInvite: (teamId: string) =>
    request<TeamInvite>(`/api/teams/${teamId}/invites`, { method: "POST" }),

  revokeInvite: (teamId: string, token: string) =>
    request<{ status: string }>(`/api/teams/${teamId}/invites/${token}`, {
      method: "DELETE",
    }),

  previewInvite: (token: string) => request<InvitePreview>(`/api/invites/${token}`),

  acceptInvite: (token: string) =>
    request<Team>(`/api/invites/${token}`, { method: "POST" }),

  teamLeaderboard: (teamId: string) =>
    request<LeaderboardEntry[]>(`/api/teams/${teamId}/leaderboard`),

  /** Every driver's best laps, readable signed out. Narrow it to plot a comparison. */
  publicLeaderboard: (filter: { track?: string; carClass?: string } = {}) => {
    const params = new URLSearchParams();
    if (filter.track) params.set("track", filter.track);
    if (filter.carClass) params.set("car_class", filter.carClass);
    const query = params.toString();
    return request<LeaderboardEntry[]>(`/api/leaderboard${query ? `?${query}` : ""}`);
  },

  adminOverview: () => request<AdminOverview>("/api/admin/overview"),

  createUpload: (filename: string, sizeBytes: number) =>
    request<{ id: string; upload_url: string }>("/api/telemetry/sessions", {
      method: "POST",
      body: JSON.stringify({ filename, size_bytes: sizeBytes }),
    }),
};

/**
 * Streams the recording straight to the API with an upload progress callback.
 * Uses XMLHttpRequest rather than fetch because fetch still cannot report upload
 * progress, and these files run to hundreds of megabytes.
 */
export function uploadRecording(
  uploadUrl: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<TelemetrySession> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/octet-stream");

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded / event.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as TelemetrySession);
        return;
      }
      let message = xhr.statusText;
      let code = "unknown";
      try {
        const body = JSON.parse(xhr.responseText);
        message = body.message ?? message;
        code = body.error ?? code;
      } catch {
        // Keep the status text.
      }
      reject(new ApiError(xhr.status, code, message));
    };

    xhr.onerror = () => reject(new ApiError(0, "network", "upload failed"));
    xhr.send(file);
  });
}
